import { supabase as defaultSupabase } from '@/lib/supabase/client';
import {
  buildGlobalReportingSummary,
  buildStoreRankings,
  hasCompleteFrozenRosterCoverage,
} from '@/lib/store-pulse/corrected-report-readers';

const STORE_PULSE_REPORTS_FUNCTION = 'store-pulse-reports';
const STORE_PULSE_RECIPIENTS_FUNCTION = 'store-pulse-recipients';
const STORE_PULSE_STORES_FUNCTION = 'store-pulse-stores';
const STORE_PULSE_NOTIFICATION_SETTINGS_FUNCTION = 'store-pulse-notification-settings';
const STORE_PULSE_SCOPE_REPORTING_FUNCTION = 'store-pulse-scope-reporting';
const STORE_PULSE_DELIVERY_STATUS_FUNCTION = 'store-pulse-delivery-status';

async function getFunctionErrorMessage(error: any): Promise<string> {
  const response = error?.context;

  if (response) {
    try {
      const payload = await response.json();
      if (payload?.error) return payload.error;
      if (payload?.message) return payload.message;
    } catch {
      // Fall through to text extraction.
    }

    try {
      const text = await response.text();
      if (text) return text;
    } catch {
      // Ignore response parsing errors.
    }
  }

  return error?.message || 'Unknown Store Pulse error';
}

async function invokeStorePulseFunction<T>(
  functionName: string,
  action: string,
  payload: object = {}
): Promise<T> {
  const { data, error } = await defaultSupabase.functions.invoke(functionName, {
    body: {
      action,
      ...payload,
    },
  });

  if (error) {
    throw new Error(await getFunctionErrorMessage(error));
  }

  return data as T;
}

export interface LocationStoreNameInput {
  location_name: string;
  store_name?: string | null;
}

export function normalizeStoreNameForMatching(value: string | null | undefined): string {
  return (value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildExactMatchIndex(storeNames: string[]): Map<string, string | null> {
  const index = new Map<string, string | null>();

  storeNames.forEach((storeName) => {
    const normalized = normalizeStoreNameForMatching(storeName);
    if (!normalized) return;

    const existing = index.get(normalized);
    if (existing && existing !== storeName) {
      index.set(normalized, null);
      return;
    }

    if (!index.has(normalized)) {
      index.set(normalized, storeName);
    }
  });

  return index;
}

export function findExactStoreNameMatch(
  locationName: string | null | undefined,
  storeNames: string[]
): string | null {
  const normalizedLocationName = normalizeStoreNameForMatching(locationName);
  if (!normalizedLocationName) return null;

  return buildExactMatchIndex(storeNames).get(normalizedLocationName) ?? null;
}

export function resolveLocationStoreName(
  location: LocationStoreNameInput,
  storeNames: string[]
): string | null {
  const explicitStoreName = location.store_name?.trim();
  if (explicitStoreName) {
    return explicitStoreName;
  }

  return findExactStoreNameMatch(location.location_name, storeNames);
}

// ============================================================================
// TYPES
// ============================================================================

export interface StoreReport {
  id: string;
  store_id: string | null;
  store_name: string;
  current_period_start: string;
  current_period_end: string;
  period_label: string;
  report_mode: string;
  current_sessions: number | null;
  prev_sessions: number | null;
  pct_change_sessions: number;
  corrected_current_sessions: number | null;
  corrected_previous_sessions: number | null;
  session_metric_available: boolean | null;
  previous_session_metric_available: boolean | null;
  session_missing_day_count: number | null;
  previous_session_missing_day_count: number | null;
  corrected_expected_store_count: number | null;
  corrected_run_id: string | null;
  current_total_jar_lifts: number | null;
  current_physical_interactions: number | null;
  physical_metric_available: boolean | null;
  physical_metric_contract: string | null;
  physical_missing_day_count: number | null;
  current_total_tap_interactions: number;
  current_total_searches: number;
  current_checkout_successes: number;
  prev_checkout_successes: number;
  current_avg_cart_size: number;
  current_top_search_terms: any;
  current_spotlight_views: number;
  current_top_spotlight_brands: any;
  current_top_spotlight_products: any;
  period_summary: {
    header?: string;
    summary?: string;
    'KEY HIGHLIGHTS (Week over Week)'?: string[];
    'DEEPER INSIGHTS'?: string[];
    key_highlights?: string[];
    deeper_insights?: string[];
  } | null;
  created_at: string;
}

export interface StoreEmailRecipient {
  id: string;
  store_id: string;
  contact_email: string;
  first_name: string | null;
  last_name: string | null;
  send_enabled: boolean;
  notes: string | null;
  created_at: string;
}

export interface StoreEmailRecipientInsert {
  store_id: string;
  contact_email: string;
  first_name?: string | null;
  last_name?: string | null;
  send_enabled?: boolean;
  notes?: string | null;
}

export interface StoreEmailRecipientUpdate {
  store_id?: string;
  contact_email?: string;
  first_name?: string | null;
  last_name?: string | null;
  send_enabled?: boolean;
  notes?: string | null;
}

export interface StoreNotificationSettings {
  id: string;
  store_id: string;
  min_sessions_for_report: number | null;
  enable_ai_summary: boolean;
  created_at: string;
  updated_at: string;
}

export interface StoreNotificationSettingsInsert {
  store_id: string;
  min_sessions_for_report?: number | null;
  enable_ai_summary?: boolean;
}

export interface StoreNotificationSettingsUpdate {
  min_sessions_for_report?: number | null;
  enable_ai_summary?: boolean;
}

export interface Store {
  id: string;
  store_slug: string;
  store_name: string;
  timezone: string | null;
  is_active: boolean;
  ac_tag_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmailDeliverySummary {
  delivery_id: string;
  environment: string;
  report_source_type: string;
  report_id: string;
  scope_id: string | null;
  store_id: string | null;
  customer_id: string | null;
  recipient_source_type: string;
  recipient_id: string | null;
  recipient_email_redacted: string | null;
  recipient_email_hash: string;
  provider: string;
  provider_message_id: string | null;
  template_key: string;
  template_version: string;
  status: string;
  test_mode: boolean;
  n8n_workflow_id: string | null;
  n8n_execution_id: string | null;
  send_requested_at: string;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
  complained_at: string | null;
  failed_at: string | null;
  skipped_at: string | null;
  error_code: string | null;
  error_message: string | null;
  store_name: string | null;
  period_start: string | null;
  period_end: string | null;
  period_label: string | null;
  report_mode: string | null;
  event_count: number;
  latest_event_type: string | null;
  latest_event_at: string | null;
  has_active_suppression: boolean;
  active_suppression_reasons: string | null;
  last_activity_at: string | null;
  last_activity_type: string | null;
}

export interface EmailWeeklySendSummary {
  environment: string;
  report_source_type: string;
  report_id: string;
  scope_id: string | null;
  store_id: string | null;
  customer_id: string | null;
  template_key: string;
  template_version: string;
  test_mode: boolean;
  n8n_workflow_id: string | null;
  n8n_execution_id: string | null;
  store_name: string | null;
  period_start: string | null;
  period_end: string | null;
  period_label: string | null;
  report_mode: string | null;
  recipient_count: number;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  skipped_count: number;
  suppressed_count: number;
  failed_count: number;
  bounced_count: number;
  complained_count: number;
  issue_count: number;
  event_count: number;
  latest_send_requested_at: string | null;
  first_send_requested_at: string | null;
  last_activity_at: string | null;
  last_activity_type: string | null;
  latest_event_type: string | null;
  latest_event_at: string | null;
  has_active_suppression: boolean;
  active_suppression_reasons: string | null;
}

export interface EmailDeliveryReconciliation {
  environment: string;
  report_source_type: string;
  report_id: string;
  scope_id: string | null;
  store_id: string | null;
  customer_id: string | null;
  template_key: string;
  template_version: string;
  expected_recipient_count: number;
  delivery_row_count: number;
  sent_count: number;
  delivered_count: number;
  sent_not_yet_delivered_count: number;
  skipped_count: number;
  suppressed_count: number;
  failed_count: number;
  bounced_count: number;
  complained_count: number;
  duplicate_count: number;
  latest_event_at: string | null;
}

export interface EmailSuppressionSummary {
  suppression_id: string;
  environment: string;
  email_redacted: string | null;
  email_hash: string;
  scope_type: string;
  customer_id: string | null;
  scope_id: string | null;
  store_id: string | null;
  reason: string;
  source: string;
  provider: string | null;
  provider_event_id: string | null;
  suppressed_at: string;
  released_at: string | null;
  released_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmailPrepareFailureSummary {
  prepare_failure_id: string;
  prepare_failure_key: string;
  environment: string;
  report_source_type: string | null;
  report_id: string | null;
  scope_id: string | null;
  store_id: string | null;
  customer_id: string | null;
  recipient_source_type: string | null;
  recipient_id: string | null;
  recipient_email_redacted: string | null;
  template_key: string | null;
  template_version: string | null;
  test_mode: boolean;
  n8n_workflow_id: string | null;
  n8n_execution_id: string | null;
  n8n_expected_recipient_count: number | null;
  n8n_recipient_batch_index: number | null;
  workflow_label: string | null;
  store_name: string | null;
  period_start: string | null;
  period_end: string | null;
  error_code: string;
  error_message: string;
  created_at: string;
  updated_at: string;
}

export interface EmailWorkflowRunSummary {
  workflow_run_id: string;
  run_key: string;
  environment: string;
  workflow_name: string | null;
  n8n_workflow_id: string;
  n8n_execution_id: string;
  run_type: string;
  current_status:
    | 'started'
    | 'selected_recipients'
    | 'sending'
    | 'completed'
    | 'failed_before_send_loop'
    | 'failed_during_send_loop';
  status_detail: string | null;
  expected_recipient_count: number | null;
  selected_recipient_count: number | null;
  sent_recipient_count: number | null;
  last_checkpoint_name: string | null;
  last_node_name: string | null;
  error_code: string | null;
  error_message: string | null;
  started_at: string | null;
  selected_recipients_at: string | null;
  sending_started_at: string | null;
  completed_at: string | null;
  failed_at: string | null;
  last_activity_at: string;
  created_at: string;
  updated_at: string;
}

export interface EmailDeliveryEvent {
  event_id: string;
  delivery_id: string;
  environment: string;
  provider: string;
  provider_message_id: string | null;
  provider_event_id: string | null;
  event_type: string;
  event_timestamp: string | null;
  url: string | null;
  webhook_signature_verified: boolean;
  recipient_email_redacted: string | null;
  raw_payload_redacted: boolean;
  created_at: string;
}

export interface EmailDeliveryListFilters {
  limit?: number;
  environment?: string;
  status?: string;
  reportId?: string;
  storeId?: string;
  scopeId?: string;
  storeName?: string;
  periodStart?: string;
  templateVersion?: string;
  n8nExecutionId?: string;
  testMode?: boolean;
  issueState?: 'all' | 'issues' | 'clean';
}

export interface EmailWorkflowRunFilters {
  limit?: number;
  environment?: string;
  status?: string;
  n8nWorkflowId?: string;
  n8nExecutionId?: string;
  runType?: string;
}

export interface EmailWeeklySendFilters {
  limit?: number;
  environment?: string;
  reportId?: string;
  storeId?: string;
  scopeId?: string;
  storeName?: string;
  periodStart?: string;
  templateVersion?: string;
  n8nExecutionId?: string;
  testMode?: boolean;
  issueState?: 'all' | 'issues' | 'clean';
}

export interface EmailDeliveryReconciliationFilters {
  limit?: number;
  environment?: string;
  reportId?: string;
  storeId?: string;
  scopeId?: string;
}

export interface EmailSuppressionFilters {
  limit?: number;
  environment?: string;
  scopeType?: string;
  reason?: string;
}

export interface ScopeReportingScopeSummary {
  id: string;
  scope_name: string;
  scope_key: string;
  scope_type: 'store' | 'group' | 'all_stores';
  store_id: string | null;
  store_name: string | null;
  customer_key: string | null;
  customer_name: string | null;
  member_store_count: number;
  recipient_count: number;
  is_active: boolean;
  email_subscribed: boolean;
}

export interface ScopeRecipientBulkUpsertInput {
  scopeIds: string[];
  contactEmail: string;
  firstName?: string | null;
  lastName?: string | null;
  sendEnabled?: boolean;
  notes?: string | null;
}

export interface ReportingPeriod {
  start: string;
  end: string | null;
  label: string;
}

export interface GlobalReportingSummary {
  periodStart: string;
  periodEnd: string | null;
  totalSessions: number | null;
  sessionChangePct: number | null;
  sessionCoverage: { available: number; total: number };
  totalCheckouts: number;
  checkoutChangePct: number | null;
}

export interface StoreMetricRanking {
  storeName: string;
  value: number;
  changePct: number | null;
}

export interface ReportingRankings {
  sessions: {
    top: StoreMetricRanking[];
    bottom: StoreMetricRanking[];
  };
  checkouts: {
    top: StoreMetricRanking[];
    bottom: StoreMetricRanking[];
  };
}

// ============================================================================
// STORE REPORTS SERVICE (Read-only)
// ============================================================================

export const storeReportsService = {
  /**
   * Get all unique store names from store_reports
   * Returns empty array if table doesn't exist (graceful degradation)
   */
  async getAvailableStoreNames(): Promise<string[]> {
    try {
      const uniqueNames = await invokeStorePulseFunction<string[]>(
        STORE_PULSE_REPORTS_FUNCTION,
        'getAvailableStoreNames'
      );
      console.log(
        '[storeReportsService] Found',
        uniqueNames.length,
        'unique store identifiers (store_name/store/account)'
      );
      return uniqueNames;
    } catch (error) {
      console.error('Error fetching store names:', error);
      return [];
    }
  },

  /**
   * Get all unique store names (for debugging)
   */
  async getAllStoreNames(): Promise<string[]> {
    try {
      return await invokeStorePulseFunction<string[]>(
        STORE_PULSE_REPORTS_FUNCTION,
        'getAvailableStoreNames'
      );
    } catch (error) {
      console.error('[storeReportsService] Exception fetching all store names:', error);
      return [];
    }
  },

  /**
   * Get distinct reporting periods (latest first)
   */
  async getAvailablePeriods(limit = 12): Promise<ReportingPeriod[]> {
    return await invokeStorePulseFunction<ReportingPeriod[]>(
      STORE_PULSE_REPORTS_FUNCTION,
      'getAvailablePeriods',
      { limit }
    );
  },

  async getReportsForPeriod(periodStart: string): Promise<StoreReport[]> {
    const reports = await invokeStorePulseFunction<StoreReport[]>(
      STORE_PULSE_REPORTS_FUNCTION,
      'getReportsForPeriod',
      { periodStart }
    );
    return hasCompleteFrozenRosterCoverage(reports)
      ? reports
      : [];
  },

  async getLatestPeriodStart(): Promise<string | null> {
    const periods = await this.getAvailablePeriods(1);
    return periods[0]?.start ?? null;
  },

  async getGlobalSummary(periodStart?: string): Promise<GlobalReportingSummary | null> {
    const targetPeriod = periodStart || (await this.getLatestPeriodStart());
    if (!targetPeriod) return null;
    const reports = await this.getReportsForPeriod(targetPeriod);
    return buildGlobalReportingSummary(reports, targetPeriod);
  },

  async getStoreRankings(periodStart?: string): Promise<ReportingRankings | null> {
    const targetPeriod = periodStart || (await this.getLatestPeriodStart());
    if (!targetPeriod) return null;
    const reports = await this.getReportsForPeriod(targetPeriod);
    return reports.length ? buildStoreRankings(reports) : null;
  },

  /**
   * Get reports for a specific store by store_name
   */
  async getReportsByStoreName(storeName: string, limit: number = 12): Promise<StoreReport[]> {
    const data = await invokeStorePulseFunction<StoreReport[]>(
      STORE_PULSE_REPORTS_FUNCTION,
      'getReportsByStoreName',
      { storeName, limit }
    );
    return data || [];
  },

  /**
   * Get latest report for a store
   */
  async getLatestReport(storeName: string): Promise<StoreReport | null> {
    return await invokeStorePulseFunction<StoreReport | null>(
      STORE_PULSE_REPORTS_FUNCTION,
      'getLatestReport',
      { storeName }
    );
  },

  /**
   * Get report by ID
   */
  async getReportById(id: string): Promise<StoreReport | null> {
    try {
      return await invokeStorePulseFunction<StoreReport | null>(
        STORE_PULSE_REPORTS_FUNCTION,
        'getReportById',
        { id }
      );
    } catch (error) {
      console.error('Error fetching report:', error);
      return null;
    }
  },

  /**
   * Get latest report for each store (batch fetch for performance)
   * Returns a map of store_name -> latest report
   */
  async getAllLatestReports(): Promise<Map<string, StoreReport>> {
    try {
      // Get all unique store names first
      const storeNames = await this.getAvailableStoreNames();
      
      if (storeNames.length === 0) {
        return new Map();
      }

      // Fetch latest report for each store in parallel
      const reportPromises = storeNames.map(async (storeName) => {
        try {
          const report = await this.getLatestReport(storeName);
          return { storeName, report };
        } catch (error) {
          console.error(`Error fetching latest report for ${storeName}:`, error);
          return { storeName, report: null };
        }
      });

      const results = await Promise.all(reportPromises);
      const reportMap = new Map<string, StoreReport>();
      
      results.forEach(({ storeName, report }) => {
        if (report) {
          reportMap.set(storeName, report);
        }
      });

      return reportMap;
    } catch (error) {
      console.error('Error fetching all latest reports:', error);
      return new Map();
    }
  },
};

// ============================================================================
// STORE EMAIL RECIPIENTS SERVICE
// ============================================================================

export const storeEmailRecipientsService = {
  async getAllWithStoresStrict(): Promise<(StoreEmailRecipient & {store_name?:string})[]> {
    return invokeStorePulseFunction(STORE_PULSE_RECIPIENTS_FUNCTION,'getAllWithStores');
  },
  async getByStoreNameStrict(storeName: string): Promise<StoreEmailRecipient[]> {
    return invokeStorePulseFunction<StoreEmailRecipient[]>(STORE_PULSE_RECIPIENTS_FUNCTION, 'getByStoreName', {storeName});
  },
  /**
   * Get recipients for a store by store_id
   */
  async getByStoreId(storeId: string): Promise<StoreEmailRecipient[]> {
    try {
      return await invokeStorePulseFunction<StoreEmailRecipient[]>(
        STORE_PULSE_RECIPIENTS_FUNCTION,
        'getByStoreId',
        { storeId }
      );
    } catch (error) {
      console.error('Error fetching email recipients:', error);
      return [];
    }
  },

  /**
   * Get recipients by store_name
   */
  async getByStoreName(storeName: string): Promise<StoreEmailRecipient[]> {
    try {
      return await invokeStorePulseFunction<StoreEmailRecipient[]>(
        STORE_PULSE_RECIPIENTS_FUNCTION,
        'getByStoreName',
        { storeName }
      );
    } catch (error) {
      console.error('Error fetching recipients by store name:', error);
      return [];
    }
  },

  /**
   * Create a new recipient by store_name
   */
  async createByStoreName(storeName: string, contactEmail: string, firstName?: string | null, lastName?: string | null): Promise<StoreEmailRecipient> {
    try {
      return await invokeStorePulseFunction<StoreEmailRecipient>(
        STORE_PULSE_RECIPIENTS_FUNCTION,
        'createByStoreName',
        {
          storeName,
          contactEmail,
          firstName: firstName ?? null,
          lastName: lastName ?? null,
        }
      );
    } catch (error) {
      console.error('Error creating email recipient:', error);
      throw error;
    }
  },

  /**
   * Create a new recipient
   */
  async create(data: StoreEmailRecipientInsert): Promise<StoreEmailRecipient> {
    try {
      return await invokeStorePulseFunction<StoreEmailRecipient>(
        STORE_PULSE_RECIPIENTS_FUNCTION,
        'create',
        data
      );
    } catch (error) {
      console.error('Error creating email recipient:', error);
      throw error;
    }
  },

  /**
   * Update a recipient
   */
  async update(id: string, data: StoreEmailRecipientUpdate, expected?: Pick<StoreEmailRecipient, 'id' | 'store_id' | 'contact_email' | 'send_enabled'>): Promise<StoreEmailRecipient> {
    try {
      if (expected) {
        const capabilities = await invokeStorePulseFunction<{conditional_recipient_email_update?:number}>(STORE_PULSE_RECIPIENTS_FUNCTION,'getCapabilities');
        if (capabilities?.conditional_recipient_email_update !== 1) throw new Error('Conditional recipient updates unavailable. Contact remains saved.');
      }
      return await invokeStorePulseFunction<StoreEmailRecipient>(
        STORE_PULSE_RECIPIENTS_FUNCTION,
        'update',
        { id, ...data, ...(expected ? {expected_preimage: {id:expected.id,store_id:expected.store_id,contact_email:expected.contact_email,send_enabled:expected.send_enabled}} : {}) }
      );
    } catch (error) {
      console.error('Error updating email recipient:', error);
      throw error;
    }
  },

  /**
   * Get all recipients by email address
   */
  async getByEmail(email: string): Promise<StoreEmailRecipient[]> {
    try {
      return await invokeStorePulseFunction<StoreEmailRecipient[]>(
        STORE_PULSE_RECIPIENTS_FUNCTION,
        'getByEmail',
        { email }
      );
    } catch (error) {
      console.error('Error fetching recipients by email:', error);
      return [];
    }
  },

  /**
   * Update all recipients with old email to new email
   */
  async updateEmailForAllRecipients(oldEmail: string, newEmail: string): Promise<number> {
    try {
      const response = await invokeStorePulseFunction<{ updatedCount: number }>(
        STORE_PULSE_RECIPIENTS_FUNCTION,
        'updateEmailForAllRecipients',
        { oldEmail, newEmail }
      );
      return response.updatedCount || 0;
    } catch (error) {
      console.error('Error updating email for all recipients:', error);
      throw error;
    }
  },

  /**
   * Get all recipients (batch fetch for performance)
   */
  async getAll(): Promise<StoreEmailRecipient[]> {
    try {
      return await invokeStorePulseFunction<StoreEmailRecipient[]>(
        STORE_PULSE_RECIPIENTS_FUNCTION,
        'getAll'
      );
    } catch (error) {
      console.error('Error fetching all email recipients:', error);
      return [];
    }
  },

  /**
   * Get all recipients with store information (joins stores table)
   */
  async getAllWithStores(): Promise<(StoreEmailRecipient & { store_name?: string })[]> {
    try {
      return await invokeStorePulseFunction<(StoreEmailRecipient & { store_name?: string })[]>(
        STORE_PULSE_RECIPIENTS_FUNCTION,
        'getAllWithStores'
      );
    } catch (error) {
      console.error('Error fetching recipients with stores:', error);
      return [];
    }
  },

  /**
   * Delete a recipient
   */
  async delete(id: string): Promise<void> {
    try {
      await invokeStorePulseFunction<{ success: boolean }>(
        STORE_PULSE_RECIPIENTS_FUNCTION,
        'delete',
        { id }
      );
    } catch (error) {
      console.error('Error deleting email recipient:', error);
      throw error;
    }
  },
};

// ============================================================================
// STORES SERVICE
// ============================================================================

export const storesService = {
  /**
   * Get all stores
   */
  async getAll(): Promise<Store[]> {
    try {
      return await invokeStorePulseFunction<Store[]>(
        STORE_PULSE_STORES_FUNCTION,
        'getAll'
      );
    } catch (error) {
      console.error('Error fetching stores:', error);
      return [];
    }
  },

  /**
   * Get store by ID
   */
  async getById(id: string): Promise<Store | null> {
    try {
      return await invokeStorePulseFunction<Store | null>(
        STORE_PULSE_STORES_FUNCTION,
        'getById',
        { id }
      );
    } catch (error) {
      console.error('Error fetching store:', error);
      return null;
    }
  },
};

// ============================================================================
// STORE NOTIFICATION SETTINGS SERVICE
// ============================================================================

export const storeNotificationSettingsService = {
  /**
   * Get settings for a store by store_id
   */
  async getByStoreId(storeId: string): Promise<StoreNotificationSettings | null> {
    try {
      return await invokeStorePulseFunction<StoreNotificationSettings | null>(
        STORE_PULSE_NOTIFICATION_SETTINGS_FUNCTION,
        'getByStoreId',
        { storeId }
      );
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      return null;
    }
  },

  /**
   * Get settings by store_name
   */
  async getByStoreName(storeName: string): Promise<StoreNotificationSettings | null> {
    try {
      return await invokeStorePulseFunction<StoreNotificationSettings | null>(
        STORE_PULSE_NOTIFICATION_SETTINGS_FUNCTION,
        'getByStoreName',
        { storeName }
      );
    } catch (error) {
      console.error('Error fetching settings by store name:', error);
      return null;
    }
  },

  /**
   * Create or update settings (upsert) by store_name
   */
  async upsertByStoreName(storeName: string, data: Omit<StoreNotificationSettingsInsert, 'store_id'>): Promise<StoreNotificationSettings> {
    try {
      return await invokeStorePulseFunction<StoreNotificationSettings>(
        STORE_PULSE_NOTIFICATION_SETTINGS_FUNCTION,
        'upsertByStoreName',
        { storeName, ...data }
      );
    } catch (error) {
      console.error('Error upserting notification settings:', error);
      throw error;
    }
  },

  /**
   * Create or update settings (upsert)
   */
  async upsert(data: StoreNotificationSettingsInsert): Promise<StoreNotificationSettings> {
    try {
      return await invokeStorePulseFunction<StoreNotificationSettings>(
        STORE_PULSE_NOTIFICATION_SETTINGS_FUNCTION,
        'upsert',
        data
      );
    } catch (error) {
      console.error('Error upserting notification settings:', error);
      throw error;
    }
  },

  /**
   * Update settings
   */
  async update(storeId: string, data: StoreNotificationSettingsUpdate): Promise<StoreNotificationSettings> {
    try {
      return await invokeStorePulseFunction<StoreNotificationSettings>(
        STORE_PULSE_NOTIFICATION_SETTINGS_FUNCTION,
        'update',
        { storeId, ...data }
      );
    } catch (error) {
      console.error('Error updating notification settings:', error);
      throw error;
    }
  },
};

// ============================================================================
// SCOPE REPORTING SERVICE
// ============================================================================

export const storeScopeReportingService = {
  /**
   * List configured scopes with recipient counts and optional email subscription state.
   */
  async listScopes(email?: string | null): Promise<ScopeReportingScopeSummary[]> {
    try {
      return await invokeStorePulseFunction<ScopeReportingScopeSummary[]>(
        STORE_PULSE_SCOPE_REPORTING_FUNCTION,
        'listScopes',
        { email: email ?? null }
      );
    } catch (error) {
      console.error('Error fetching scope reporting scopes:', error);
      return [];
    }
  },

  /**
   * Upsert one recipient across many scopes.
   */
  async upsertRecipientForScopes(input: ScopeRecipientBulkUpsertInput): Promise<{ upsertedCount: number }> {
    try {
      return await invokeStorePulseFunction<{ upsertedCount: number }>(
        STORE_PULSE_SCOPE_REPORTING_FUNCTION,
        'upsertRecipientForScopes',
        input
      );
    } catch (error) {
      console.error('Error upserting scope recipients:', error);
      throw error;
    }
  },

  /**
   * Remove one recipient from many scopes.
   */
  async removeRecipientFromScopes(scopeIds: string[], contactEmail: string): Promise<{ removedCount: number }> {
    try {
      return await invokeStorePulseFunction<{ removedCount: number }>(
        STORE_PULSE_SCOPE_REPORTING_FUNCTION,
        'removeRecipientFromScopes',
        {
          scopeIds,
          contactEmail,
        }
      );
    } catch (error) {
      console.error('Error removing scope recipients:', error);
      throw error;
    }
  },
};

// ============================================================================
// STORE EMAIL DELIVERY STATUS SERVICE
// ============================================================================

export const storeEmailDeliveryStatusService = {
  async getWeeklySends(filters: EmailWeeklySendFilters = {}): Promise<EmailWeeklySendSummary[]> {
    try {
      return await invokeStorePulseFunction<EmailWeeklySendSummary[]>(
        STORE_PULSE_DELIVERY_STATUS_FUNCTION,
        'getWeeklySends',
        filters
      );
    } catch (error) {
      console.error('Error fetching weekly email sends:', error);
      throw error;
    }
  },

  async getRecentDeliveries(filters: EmailDeliveryListFilters = {}): Promise<EmailDeliverySummary[]> {
    try {
      return await invokeStorePulseFunction<EmailDeliverySummary[]>(
        STORE_PULSE_DELIVERY_STATUS_FUNCTION,
        'getRecentDeliveries',
        filters
      );
    } catch (error) {
      console.error('Error fetching email delivery status:', error);
      throw error;
    }
  },

  async getDeliveryEvents(deliveryId: string, limit = 100): Promise<EmailDeliveryEvent[]> {
    try {
      return await invokeStorePulseFunction<EmailDeliveryEvent[]>(
        STORE_PULSE_DELIVERY_STATUS_FUNCTION,
        'getDeliveryEvents',
        { deliveryId, limit }
      );
    } catch (error) {
      console.error('Error fetching email delivery events:', error);
      throw error;
    }
  },

  async getReconciliation(
    filters: EmailDeliveryReconciliationFilters = {}
  ): Promise<EmailDeliveryReconciliation[]> {
    try {
      return await invokeStorePulseFunction<EmailDeliveryReconciliation[]>(
        STORE_PULSE_DELIVERY_STATUS_FUNCTION,
        'getReconciliation',
        filters
      );
    } catch (error) {
      console.error('Error fetching email delivery reconciliation:', error);
      throw error;
    }
  },

  async getActiveSuppressions(filters: EmailSuppressionFilters = {}): Promise<EmailSuppressionSummary[]> {
    try {
      return await invokeStorePulseFunction<EmailSuppressionSummary[]>(
        STORE_PULSE_DELIVERY_STATUS_FUNCTION,
        'getActiveSuppressions',
        filters
      );
    } catch (error) {
      console.error('Error fetching email suppressions:', error);
      throw error;
    }
  },

  async getPrepareFailures(filters: EmailDeliveryListFilters = {}): Promise<EmailPrepareFailureSummary[]> {
    try {
      return await invokeStorePulseFunction<EmailPrepareFailureSummary[]>(
        STORE_PULSE_DELIVERY_STATUS_FUNCTION,
        'getPrepareFailures',
        filters
      );
    } catch (error) {
      console.error('Error fetching email prepare failures:', error);
      throw error;
    }
  },

  async getWorkflowRuns(filters: EmailWorkflowRunFilters = {}): Promise<EmailWorkflowRunSummary[]> {
    try {
      return await invokeStorePulseFunction<EmailWorkflowRunSummary[]>(
        STORE_PULSE_DELIVERY_STATUS_FUNCTION,
        'getWorkflowRuns',
        filters
      );
    } catch (error) {
      console.error('Error fetching email workflow runs:', error);
      throw error;
    }
  },

  async getRetryCandidates(filters: EmailDeliveryListFilters = {}): Promise<EmailDeliverySummary[]> {
    try {
      return await invokeStorePulseFunction<EmailDeliverySummary[]>(
        STORE_PULSE_DELIVERY_STATUS_FUNCTION,
        'getRetryCandidates',
        filters
      );
    } catch (error) {
      console.error('Error fetching email retry candidates:', error);
      throw error;
    }
  },
};

// ============================================================================
// STORE NAME AUTO-MATCHING
// ============================================================================

export const storeNameMatchingService = {
  /**
   * Resolve location_name to store_name using exact normalized matching only.
   * Ambiguous, partial, and fuzzy matches intentionally return null so the UI fails closed.
   */
  async findMatchingStoreName(locationName: string): Promise<string | null> {
    try {
      const availableStoreNames = await storeReportsService.getAvailableStoreNames();
      const exactReportMatch = findExactStoreNameMatch(locationName, availableStoreNames);
      if (exactReportMatch) {
        return exactReportMatch;
      }

      try {
        const stores = await storesService.getAll();
        return findExactStoreNameMatch(
          locationName,
          stores.map((store) => store.store_name).filter(Boolean)
        );
      } catch (storesError) {
        console.warn('stores fallback lookup failed:', storesError);
      }

      return null;
    } catch (error) {
      console.error('Error finding matching store name:', error);
      return null;
    }
  },

  resolveLocationStoreName(location: LocationStoreNameInput, storeNames: string[]): string | null {
    return resolveLocationStoreName(location, storeNames);
  },
};
